import { useState, useEffect } from 'react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/Button'
import { ProposalStudio } from '@/components/Deals/ProposalStudio'
import { Search, Layers, FilePlus, Save, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'
import api from '@/services/api'
import type { Deal } from '@/types'

interface PlanOption {
  id: string
  nome: string
}

export default function ProposalStudioPage() {
  const [deals, setDeals] = useState<Deal[]>([])
  const [plans, setPlans] = useState<PlanOption[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedDealId, setSelectedDealId] = useState<string>('')
  const [studioOpen, setStudioOpen] = useState(false)
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    async function loadData() {
      try {
        const [dealsResp, plansResp] = await Promise.all([
          api.get('/deals'),
          api.get('/plans'),
        ])
        setDeals(dealsResp.data.data ?? dealsResp.data ?? [])
        setPlans(plansResp.data.data ?? plansResp.data ?? [])
      } catch {
        toast.error('Erro ao carregar dados')
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  const selectedDeal = deals.find((d) => d.id === selectedDealId)

  const filteredDeals = deals.filter((d) => {
    if (!searchTerm) return true
    const s = searchTerm.toLowerCase()
    return (
      d.empresaNome?.toLowerCase().includes(s) ||
      d.nome?.toLowerCase().includes(s) ||
      d.servico?.toLowerCase().includes(s)
    )
  })

  const handleSaveAsTemplate = async () => {
    if (!selectedDeal) {
      toast.error('Selecione um deal primeiro')
      return
    }
    setSavingTemplate(true)
    try {
      await api.post('/proposal-templates', {
        dealId: selectedDeal.id,
        empresaNome: selectedDeal.empresaNome,
        nome: `Template - ${selectedDeal.empresaNome}`,
      })
      toast.success('Template salvo com sucesso!')
    } catch {
      toast.error('Erro ao salvar template')
    } finally {
      setSavingTemplate(false)
    }
  }

  // Mock deal for quick-start without selecting an existing deal
  const mockDeal: Deal = {
    id: 'mock-studio',
    nome: 'Novo Projeto',
    empresaId: '',
    empresaNome: selectedDeal?.empresaNome || 'Nova Empresa',
    closerId: '',
    closerNome: '',
    valor: 0,
    probabilidade: 0,
    etapa: 'proposta_elaboracao',
    servico: selectedDeal?.servico || 'Consultoria',
    stagnant: false,
    driftDays: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }

  const dealForStudio = selectedDeal || mockDeal

  return (
    <DashboardLayout title="Proposal Studio">
      {studioOpen ? (
        <ProposalStudio
          deal={dealForStudio}
          onClose={() => setStudioOpen(false)}
          onSave={() => {
            toast.success('Proposta salva do Studio!')
            setStudioOpen(false)
          }}
        />
      ) : (
        <div className="space-y-5">
          {/* Hero */}
          <div className="glass-card p-6">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
                    <Layers size={16} className="text-accent" />
                  </div>
                  <h2 className="text-lg font-bold text-text-primary">Proposal Studio</h2>
                </div>
                <p className="text-sm text-text-muted pl-10">
                  Crie propostas visuais com layout customizado, combinando planos e serviços em apresentações de alto impacto.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<Save size={13} />}
                  loading={savingTemplate}
                  onClick={handleSaveAsTemplate}
                  disabled={!selectedDeal}
                >
                  Salvar como Template
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  icon={<ExternalLink size={14} />}
                  onClick={() => setStudioOpen(true)}
                >
                  Abrir Studio
                </Button>
              </div>
            </div>
          </div>

          {/* Deal Selector */}
          <div className="glass-card p-5">
            <h3 className="text-xs font-bold text-accent uppercase tracking-wider mb-4">
              Selecionar Deal
            </h3>

            {loading ? (
              <p className="text-sm text-text-muted">Carregando deals...</p>
            ) : (
              <div className="space-y-4">
                {/* Search */}
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                  <input
                    type="text"
                    placeholder="Buscar deal por empresa, nome ou serviço..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:border-accent transition-colors"
                  />
                </div>

                {/* Deal list */}
                {filteredDeals.length === 0 ? (
                  <div className="text-center py-8 text-text-muted">
                    <Layers size={32} className="mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Nenhum deal encontrado</p>
                    <p className="text-xs mt-1">Selecione um deal existente ou use o modo avulso</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto premium-scroll">
                    {filteredDeals.map((deal) => (
                      <button
                        key={deal.id}
                        onClick={() => setSelectedDealId(deal.id)}
                        className={`text-left p-3 rounded-lg border transition-all ${
                          selectedDealId === deal.id
                            ? 'border-accent/50 bg-accent/10'
                            : 'border-white/[0.06] bg-white/[0.03] hover:border-white/[0.12] hover:bg-white/[0.05]'
                        }`}
                      >
                        <p className="text-sm font-medium text-text-primary truncate">
                          {deal.empresaNome}
                        </p>
                        <p className="text-[11px] text-text-muted truncate mt-0.5">
                          {deal.servico || '—'} · {deal.nome}
                        </p>
                      </button>
                    ))}
                  </div>
                )}

                {/* Quick start without deal */}
                <div className="border-t border-white/[0.06] pt-4">
                  <p className="text-xs text-text-muted mb-2">
                    Ou comece sem selecionar um deal:
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<FilePlus size={13} />}
                    onClick={() => {
                      setSelectedDealId('')
                      setStudioOpen(true)
                    }}
                  >
                    Modo Avulso (Sem Deal)
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Available Plans Summary */}
          {plans.length > 0 && (
            <div className="glass-card p-5">
              <h3 className="text-xs font-bold text-accent uppercase tracking-wider mb-3">
                Planos Disponíveis ({plans.length})
              </h3>
              <div className="flex flex-wrap gap-2">
                {plans.map((plan) => (
                  <span
                    key={plan.id}
                    className="px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.06] text-xs text-text-primary"
                  >
                    {plan.nome}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Info / Instructions */}
          <div className="glass-card p-5">
            <h3 className="text-xs font-bold text-accent uppercase tracking-wider mb-2">
              Como usar
            </h3>
            <ul className="space-y-1.5 text-sm text-text-muted">
              <li>1. Selecione um deal existente ou use o <strong>Modo Avulso</strong></li>
              <li>2. Clique em <strong>Abrir Studio</strong> para abrir o editor visual</li>
              <li>3. Adicione planos e serviços, ajuste o layout dos slides</li>
              <li>4. Salve a proposta ou exporte como <strong>Template</strong> para reuso</li>
            </ul>
          </div>
        </div>
      )}
    </DashboardLayout>
  )
}
